import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend }
from 'chart.js';
import { useGlobalContext } from '../GlobalContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Home = () => {
  const { periode, api, isReady } = useGlobalContext();
  const [simpananData, setSimpananData] = useState([]);
  const [manasukaData, setManasukaData] = useState([]);
  const [hariRayaData, setHariRayaData] = useState([]);

  // === Ambil data Simpanan Pokok & Wajib ===
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/api/grafik/simpananpw/${periode}`);
        const data = res.data || [];
        data.sort((a, b) => a.periode.localeCompare(b.periode));
        setSimpananData(data);
      } catch (error) {
        console.error('❌ Gagal mengambil data simpanan:', error);
      }
    };

    if (isReady && periode) fetchData();
  }, [isReady, periode, api]);

  // === Ambil data Simpanan Manasuka ===
  useEffect(() => {
    const fetchManasuka = async () => {
      try {
        const res = await api.get(`/api/grafik/simpananmanasuka/${periode}`);
        const data = res.data || [];
        data.sort((a, b) => a.periode.localeCompare(b.periode));
        setManasukaData(data);
      } catch (error) {
        console.error('❌ Gagal mengambil data manasuka:', error);
      }
    };

    if (isReady && periode) fetchManasuka();
  }, [isReady, periode, api]);

  // === Ambil data Simpanan Hari Raya ===
  useEffect(() => {
    const fetchHariRaya = async () => {
      try {
        const res = await api.get(`/api/grafik/simpananhariraya/${periode}`);
        const data = res.data || [];
        data.sort((a, b) => a.periode.localeCompare(b.periode));
        setHariRayaData(data);
      } catch (error) {
        console.error('❌ Gagal mengambil data hari raya:', error);
      }
    };

    if (isReady && periode) fetchHariRaya();
  }, [isReady, periode, api]);

  // === Olah data Simpanan Pokok & Wajib ===
  const labels = simpananData.map(item => 
    `${item.periode.substring(4, 6)}/${item.periode.substring(0, 4)}`
  );

  const simpananPokok = simpananData.map(item => 
    parseFloat((item.simppokok / 1_000_000).toFixed(1))
  );

  const simpananWajib = simpananData.map(item => 
    parseFloat((item.simpwajib / 1_000_000).toFixed(1))
  );

  const barChartData = {
    labels,
    datasets: [
      {
        label: 'Simpanan Pokok (Juta)',
        backgroundColor: 'rgba(54, 162, 235, 0.9)',
        borderColor: 'rgba(54, 162, 235, 0.8)',
        data: simpananPokok,
      },
      {
        label: 'Simpanan Wajib (Juta)',
        backgroundColor: 'rgba(255, 206, 86, 0.9)',
        borderColor: 'rgba(255, 206, 86, 0.8)',
        data: simpananWajib,
      }
    ],
  };

  // === Olah data Manasuka ===
  const manasukaLabels = manasukaData.map(item => 
    `${item.periode.substring(4, 6)}/${item.periode.substring(0, 4)}`
  );

  const tabungan = manasukaData.map(item => 
    parseFloat((item.tabungan / 1_000_000).toFixed(1))
  );

  const jasa = manasukaData.map(item => 
    parseFloat((item.jasa / 1_000_000).toFixed(1))
  );

  const manasukaChartData = {
    labels: manasukaLabels,
    datasets: [
      {
        label: 'Tabungan (Juta)',
        backgroundColor: 'rgba(75, 192, 192, 0.9)',
        borderColor: 'rgba(75, 192, 192, 0.8)',
        data: tabungan,
      },
      {
        label: 'Jasa (Juta)',
        backgroundColor: 'rgba(153, 102, 255, 0.9)',
        borderColor: 'rgba(153, 102, 255, 0.8)',
        data: jasa,
      }
    ],
  };

  // === Olah data Hari Raya ===
  const hariRayaLabels = hariRayaData.map(item => 
    `${item.periode.substring(4, 6)}/${item.periode.substring(0, 4)}`
  );

  const hariTabungan = hariRayaData.map(item => 
    parseFloat((item.tabungan / 1_000_000).toFixed(1))
  );

  const hariJasa = hariRayaData.map(item => 
    parseFloat((item.jasa / 1_000_000).toFixed(1))
  );

  const hariRayaChartData = {
    labels: hariRayaLabels,
    datasets: [
      {
        label: 'Tabungan Hari Raya (Juta)',
        backgroundColor: 'rgba(0, 119, 255, 0.56)',
        borderColor: 'rgba(0, 119, 255, 0.56)',
        data: hariTabungan,
      },
      {
        label: 'Jasa Hari Raya (Juta)',
        backgroundColor: 'rgba(255, 0, 0, 0.9)',
        borderColor: 'rgba(255, 0, 0, 0.9)',
        data: hariJasa,
      }
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { 
      legend: { position: 'top' },
    },
    scales: {
      x: { stacked: true, ticks: { autoSkip: false } },
      y: { stacked: true, beginAtZero: true },
    },
  };

  // Fungsi untuk format angka ke Rupiah
  const formatRupiah = (number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(number);
  };

  return (
    <div className="wrapper">
      {/* Header */}
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-2">Dashboard Simpanan</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="#">Home</a></li>
                <li className="breadcrumb-item active">Dashboard</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="content">
        <div className="container-fluid">
          <div className="row">
            
            {/* === KELOMPOK SIMPANAN POKOK & WAJIB === */}
            <div className="col-md-6">
              {/* Grafik Pokok & Wajib */}
              <div className="card card-success">
                <div className="card-header">
                  <h3 className="card-title">Grafik Simpanan Pokok & Wajib</h3>
                  <span className="ml-3">Periode: {periode}</span>
                </div>
                <div className="card-body">
                  <div className="chart" style={{ height: 300 }}>
                    <Bar data={barChartData} options={options} height={300} />
                  </div>
                </div>
              </div>
              
              {/* Tabel Data Simpanan Pokok & Wajib */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Tabel Data Simpanan Pokok & Wajib</h3>
                </div>
                <div className="card-body p-0">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Periode</th>
                        <th>Simpanan Pokok</th>
                        <th>Simpanan Wajib</th>
                      </tr>
                    </thead>
                    <tbody>
                      {simpananData.map(item => (
                        <tr key={item.periode}>
                          <td>{`${item.periode.substring(4, 6)}/${item.periode.substring(0, 4)}`}</td>
                          <td>{formatRupiah(item.simppokok)}</td>
                          <td>{formatRupiah(item.simpwajib)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* === KELOMPOK SIMPANAN MANASUKA === */}
            <div className="col-md-6">
              {/* Grafik Manasuka */}
              <div className="card card-info">
                <div className="card-header">
                  <h3 className="card-title">Grafik Simpanan Manasuka (Tabungan & Jasa)</h3>
                  <span className="ml-3">Periode: {periode}</span>
                </div>
                <div className="card-body">
                  <div className="chart" style={{ height: 300 }}>
                    <Bar data={manasukaChartData} options={options} height={300} />
                  </div>
                </div>
              </div>
              
              {/* Tabel Data Simpanan Manasuka */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Tabel Data Simpanan Manasuka</h3>
                </div>
                <div className="card-body p-0">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Periode</th>
                        <th>Tabungan</th>
                        <th>Jasa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manasukaData.map(item => (
                        <tr key={item.periode}>
                          <td>{`${item.periode.substring(4, 6)}/${item.periode.substring(0, 4)}`}</td>
                          <td>{formatRupiah(item.tabungan)}</td>
                          <td>{formatRupiah(item.jasa)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* === KELOMPOK SIMPANAN HARI RAYA === */}
            <div className="col-md-6"> 
              {/* Grafik Hari Raya */}
              <div className="card card-warning">
                <div className="card-header">
                  <h3 className="card-title">Grafik Simpanan Hari Raya (Tabungan & Jasa)</h3>
                  <span className="ml-3">Periode: {periode}</span>
                </div>
                <div className="card-body">
                  <div className="chart" style={{ height: 300 }}>
                    <Bar data={hariRayaChartData} options={options} height={300} />
                  </div>
                </div>
              </div>
              
              {/* Tabel Data Simpanan Hari Raya */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Tabel Data Simpanan Hari Raya</h3>
                </div>
                <div className="card-body p-0">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Periode</th>
                        <th>Tabungan</th>
                        <th>Jasa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hariRayaData.map(item => (
                        <tr key={item.periode}>
                          <td>{`${item.periode.substring(4, 6)}/${item.periode.substring(0, 4)}`}</td>
                          <td>{formatRupiah(item.tabungan)}</td>
                          <td>{formatRupiah(item.jasa)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;